import React, { useEffect, useMemo, useState } from "react";
import CryptoJS from "crypto-js";
import api from "../api";
import "./estilodash.css";

const gerarChavePadrao = (conta) =>
  CryptoJS.MD5(String(Number(conta) * 11 - 181818)).toString(
    CryptoJS.enc.Hex
  );

const GERADORES = {
  ea: gerarChavePadrao,
  bias: gerarChavePadrao,
  ragnarok: gerarChavePadrao,
};

export default function AdicionaLicencas() {
  const [clientes, setClientes] = useState([]);
  const [assinaturas, setAssinaturas] = useState([]);
  const [licencas, setLicencas] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [pacotes, setPacotes] = useState([]);

  const [clienteId, setClienteId] = useState("");
  const [assinaturaId, setAssinaturaId] = useState("");
  const [produtoId, setProdutoId] = useState("");
  const [produtoCodigo, setProdutoCodigo] = useState("");

  const [conta, setConta] = useState("");
  const [chave, setChave] = useState("");
  const [tipo, setTipo] = useState("Real");
  const [status, setStatus] = useState("1");
  const [dateExp, setDateExp] = useState("");

  const [msg, setMsg] = useState("");
  const [erro, setErro] = useState("");
  const [carregando, setCarregando] = useState(false);

  useEffect(() => {
    carregarTudo();
  }, []);

  const carregarTudo = async () => {
    try {
      setCarregando(true);
      setErro("");
      setMsg("");

      const [c, l, p, pac] = await Promise.all([
        api.get("/api/clientes"),
        api.get("/api/licencas-v2"),
        api.get("/api/produtos-ativos"),
        api.get("/api/pacotes-ativos"),
      ]);

      setClientes(c.data || []);
      setLicencas(l.data || []);
      setProdutos(p.data || []);
      setPacotes(pac.data || []);
      setAssinaturas([]);
    } catch (err) {
      console.error("Erro ao carregar dados de licenciamento:", err);
      setErro("Erro ao carregar dados. Verifique o servidor/login.");
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    const carregarAssinaturasCliente = async () => {
      if (!clienteId) {
        setAssinaturas([]);
        setAssinaturaId("");
        setProdutoId("");
        setProdutoCodigo("");
        return;
      }

      try {
        setErro("");
        const resp = await api.get(`/api/assinaturas/cliente/${clienteId}`);
        const lista = (resp.data || []).filter(
          (a) => Number(a.status ?? a.assinatura_status ?? 0) === 1
        );
        setAssinaturas(lista);
      } catch (err) {
        console.error("Erro ao carregar assinaturas do cliente:", err);
        setAssinaturas([]);
        setErro("Erro ao carregar assinaturas do cliente.");
      }
    };

    carregarAssinaturasCliente();
  }, [clienteId]);

  const clienteSelecionado = useMemo(() => {
    return clientes.find((c) => String(c.id) === String(clienteId)) || null;
  }, [clientes, clienteId]);

  const assinaturasCliente = useMemo(() => {
    return assinaturas;
  }, [assinaturas]);

  const assinaturaAtual = useMemo(() => {
    return (
      assinaturasCliente.find((a) => String(a.id) === String(assinaturaId)) ||
      null
    );
  }, [assinaturasCliente, assinaturaId]);

  const produtosPermitidos = useMemo(() => {
    if (!assinaturaAtual) return [];

    if (assinaturaAtual.produto_id) {
      const prodId = Number(assinaturaAtual.produto_id);
      const produtoApi = produtos.find((p) => Number(p.id) === prodId);

      return [
        {
          id: prodId,
          codigo: produtoApi?.codigo || assinaturaAtual.codigo_plano || "",
          nome: produtoApi?.nome || assinaturaAtual.nome_plano || "Produto",
        },
      ];
    }

    if (assinaturaAtual.pacote_id) {
      const pacoteApi = pacotes.find(
        (p) => Number(p.id) === Number(assinaturaAtual.pacote_id)
      );

      if (pacoteApi && Array.isArray(pacoteApi.produtos)) {
        return pacoteApi.produtos.map((p) => ({
          id: Number(p.id),
          codigo: p.codigo,
          nome: p.nome,
        }));
      }
    }

    return [];
  }, [assinaturaAtual, produtos, pacotes]);

  useEffect(() => {
    if (assinaturasCliente.length === 1) {
      const unica = String(assinaturasCliente[0].id);
      if (assinaturaId !== unica) {
        setAssinaturaId(unica);
      }
    } else if (
      assinaturaId &&
      !assinaturasCliente.some((a) => String(a.id) === String(assinaturaId))
    ) {
      setAssinaturaId("");
    }
  }, [assinaturasCliente, assinaturaId]);

  useEffect(() => {
    if (produtosPermitidos.length === 1) {
      const unico = produtosPermitidos[0];
      if (String(produtoId) !== String(unico.id)) {
        setProdutoId(String(unico.id));
        setProdutoCodigo(unico.codigo || "");
      }
    } else if (
      produtoId &&
      !produtosPermitidos.some((p) => String(p.id) === String(produtoId))
    ) {
      setProdutoId("");
      setProdutoCodigo("");
    }
  }, [produtosPermitidos, produtoId]);

  const resumoProduto = useMemo(() => {
    if (!assinaturaAtual || !produtoId) return null;

    const slotsContratados = Number(assinaturaAtual.slots_contratados || 0);

    const licencasAtivas = licencas.filter(
      (l) =>
        String(l.cliente_id) === String(clienteId) &&
        String(l.produto_id) === String(produtoId) &&
        String(l.status) === "1"
    ).length;

    const slotsDisponiveis = Math.max(slotsContratados - licencasAtivas, 0);

    return {
      slotsContratados,
      licencasAtivas,
      slotsDisponiveis,
    };
  }, [assinaturaAtual, licencas, clienteId, produtoId]);

  const resetCamposLicenca = () => {
    setConta("");
    setChave("");
    setTipo("Real");
    setStatus("1");
    setDateExp("");
  };

  const handleTrocarCliente = (value) => {
    setClienteId(value);
    setAssinaturaId("");
    setProdutoId("");
    setProdutoCodigo("");
    resetCamposLicenca();
    setMsg("");
    setErro("");
  };

  const handleTrocarAssinatura = (value) => {
    setAssinaturaId(value);
    setProdutoId("");
    setProdutoCodigo("");
    resetCamposLicenca();
    setMsg("");
    setErro("");
  };

  const handleTrocarProduto = (value) => {
    const p = produtosPermitidos.find((x) => String(x.id) === String(value));
    setProdutoId(value);
    setProdutoCodigo(p?.codigo || "");
    setChave("");
    setMsg("");
    setErro("");
  };

  const handleGerar = () => {
    if (!produtoCodigo) {
      alert("Selecione o produto/robô.");
      return;
    }

    if (!conta) {
      alert("Informe a conta MT5.");
      return;
    }

    const contaNum = Number(conta);
    if (Number.isNaN(contaNum)) {
      alert("Conta inválida. Use apenas números.");
      return;
    }

    const fn = GERADORES[produtoCodigo] || GERADORES.ea;
    setChave(fn(conta));
  };

  const salvar = async (e) => {
    e.preventDefault();
    setMsg("");
    setErro("");

    if (!clienteId) {
      setErro("Selecione o cliente.");
      return;
    }

    if (!assinaturaId) {
      setErro("Selecione a assinatura.");
      return;
    }

    if (!produtoId) {
      setErro("Selecione o produto/robô.");
      return;
    }

    if (!conta) {
      setErro("Informe a conta MT5.");
      return;
    }

    if (!chave) {
      setErro("Gere a chave antes de salvar.");
      return;
    }

    if (resumoProduto && resumoProduto.slotsDisponiveis <= 0) {
      setErro(
        "Não há slots disponíveis para este produto. Altere a assinatura do cliente antes de adicionar nova licença."
      );
      return;
    }

    const duplicada = licencas.find(
      (l) =>
        String(l.conta) === String(conta) &&
        String(l.produto_id) === String(produtoId)
    );

    if (duplicada) {
      setErro("Já existe licença para essa conta nesse produto.");
      return;
    }

    try {
      setCarregando(true);

      await api.post("/api/licencas-v2", {
        cliente_id: Number(clienteId),
        assinatura_id: Number(assinaturaId),
        produto_id: Number(produtoId),
        conta,
        lkey: chave,
        status,
        tipo,
        date_exp: dateExp || null,
      });

      setMsg("Licença criada com sucesso.");
      resetCamposLicenca();

      const l = await api.get("/api/licencas-v2");
      setLicencas(l.data || []);
    } catch (err) {
      console.error("Erro ao salvar licença:", err);
      setErro(
        err.response?.data?.message || "Erro ao salvar licença."
      );
    } finally {
      setCarregando(false);
    }
  };

  return (
    <div className="pagamentos-container">
      <h2>Adicionar Licença</h2>

      <p>
        Selecione o cliente, selecione o produto disponível, digite o número de
        sua conta MT5, clique em <strong>Gerar Chave</strong>, selecione o tipo
        de licença (Demo ou Real), e clique em <strong>Salvar Licença</strong>.
      </p>

      {msg && (
        <div className="qt-admin-alert qt-admin-alert-success" role="status">
          {msg}
        </div>
      )}

      {erro && (
        <div className="qt-admin-alert qt-admin-alert-error" role="alert">
          {erro}
        </div>
      )}

      {carregando && <p>Carregando...</p>}

      <form onSubmit={salvar} className="qt-form-panel qt-admin-form qt-form-licenca">
        <div className="form-linha">
          <label>
            Cliente
            <select
              value={clienteId}
              onChange={(e) => handleTrocarCliente(e.target.value)}
            >
              <option value="">Selecione</option>
              {[...clientes]
                .sort((a, b) =>
                  String(a.nome || "").localeCompare(
                    String(b.nome || ""),
                    "pt-BR"
                  )
                )
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.id} - {c.nome}
                  </option>
                ))}
            </select>
          </label>

          <label>
            Assinatura
            <select
              value={assinaturaId}
              onChange={(e) => handleTrocarAssinatura(e.target.value)}
              disabled={!clienteId || assinaturasCliente.length === 1}
            >
              <option value="">Selecione</option>
              {assinaturasCliente.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.nome_plano} ({a.tier})
                </option>
              ))}
            </select>
          </label>
        </div>

        {clienteSelecionado && (
          <div className="qt-admin-notice qt-admin-notice-info">
            <strong>Cliente:</strong> {clienteSelecionado.nome} <br />
            <strong>Assinaturas ativas:</strong> {assinaturasCliente.length}
          </div>
        )}

        <div className="form-linha">
          <label>
            Produto / Robô
            <select
              value={produtoId}
              onChange={(e) => handleTrocarProduto(e.target.value)}
              disabled={!assinaturaId}
            >
              <option value="">Selecione</option>
              {produtosPermitidos.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nome}
                </option>
              ))}
            </select>
          </label>

          <label>
            Conta MT5
            <input
              value={conta}
              onChange={(e) => setConta(e.target.value)}
              placeholder="Digite a conta MT5"
            />
          </label>
        </div>

        {produtoId && resumoProduto && (
          <div className="qt-admin-notice qt-admin-notice-warning qt-admin-license-summary">
            <strong>Produto Ativo:</strong>{" "}
            {produtosPermitidos.find((p) => String(p.id) === String(produtoId))
              ?.nome || "—"}{" "}
            | <strong>Slots contratados:</strong> {resumoProduto.slotsContratados}{" "}
            | <strong>Licenças ativas:</strong> {resumoProduto.licencasAtivas}{" "}
            | <strong>Slots disponíveis:</strong> {resumoProduto.slotsDisponiveis}
          </div>
        )}

        <div className="form-linha">
          <label>
            Tipo
            <select value={tipo} onChange={(e) => setTipo(e.target.value)}>
              <option value="Real">Real</option>
              <option value="Demo">Demo</option>
            </select>
          </label>

          <label>
            Status
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="1">Ativa</option>
              <option value="0">Inativa</option>
            </select>
          </label>

          <label>
            Expiração
            <input
              type="date"
              value={dateExp}
              onChange={(e) => setDateExp(e.target.value)}
            />
          </label>
        </div>

        <div className="form-linha">
          <label>
            Chave (lkey)
            <input
              value={chave}
              onChange={(e) => setChave(e.target.value)}
              placeholder="Clique em Gerar Chave"
            />
          </label>
        </div>

        <div className="barra-acoes-pagamento">
          <button type="button" className="qt-btn qt-btn-secondary" onClick={handleGerar}>
            Gerar Chave
          </button>
          <button type="submit" className="qt-btn qt-btn-primary">Salvar Licença</button>
        </div>
      </form>
    </div>
  );
}
